import React, { useEffect, useRef, useState } from 'react'

import {
    Autocomplete,
    Box,
    Button,
    ClickAwayListener,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    IconButton,
    Paper,
    Popper,
    Typography,
} from '@mui/material'

import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import api from '../../../api';
import { getSession } from '../../../auth';
import IntegerOnly from '../../../utils/IntegerOnly';
import VariableTextField from '../../Input/VariableTextField';
import OpportunityMarkers from '../../Journeys/opportunity-markers';
import NewCustomFieldForm from '../../Journeys/NewCustomFieldForm';
import useNotification from '../../Notification';

const ACTION_OPTIONS = [
    { value: 'ADD_CONTACT_GROUP', label: 'Adicionar contato a segmento' },
    { value: 'ADD_CHAT_LABEL', label: 'Adicionar etiqueta ao atendimento' },
    { value: 'CREATE_OPPORTUNITY', label: 'Criar oportunidade' },
]
const ACTION_OPTION_VALUES = ACTION_OPTIONS.map(option => option.value)

const getEmptyAction = () => ({
    action: 'ADD_CONTACT_GROUP',
    timeout: null,
    label_chats: [],
    label_chat_options: [],
    contact_groups: [],
    contact_group_options: [],
    journey: '',
    journey_column: '',
    opportunity_title: '',
    opportunity_description: '',
    opportunity_value: '',
    opportunity_tags: [],
    opportunity_custom_fields: {},
})

const actionNeedsLabel = (action) => action === 'ADD_CHAT_LABEL'
const actionNeedsGroup = (action) => action === 'ADD_CONTACT_GROUP'
const actionNeedsOpportunity = (action) => action === 'CREATE_OPPORTUNITY'

const getOptionLabel = (option) => option?.name || option?.label || ''

const normalizeIdList = (value) => {
    if (Array.isArray(value))
        return value.filter(each => each !== undefined && each !== null && each !== '').map(each => String(each))
    if (value === undefined || value === null || value === '')
        return []
    return [String(value)]
}

const normalizeCustomFields = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {}
    return Object.fromEntries(Object.entries(value).map(([key, item]) => ([String(key), item])))
}

const getActionTargetIds = (action) => actionNeedsLabel(action.action)
    ? normalizeIdList(Object.prototype.hasOwnProperty.call(action, 'label_chats') ? action.label_chats : action.label_chat)
    : normalizeIdList(Object.prototype.hasOwnProperty.call(action, 'contact_groups') ? action.contact_groups : action.contact_group)

const getActionTargetOptions = (action) => actionNeedsLabel(action.action)
    ? (action.label_chat_options || [])
    : (action.contact_group_options || [])

const pushUniqueOption = (options, option) => {
    if (!option?.id || options.some(each => String(each.id) === String(option.id)))
        return options
    return [...options, option]
}

export const normalizeEscalationActions = (actions) => {
    const groupedActions = []
    const actionByKey = new Map()

    ;(actions || []).forEach(action => {
        const normalizedAction = action.action || 'ADD_CONTACT_GROUP'
        if (!ACTION_OPTION_VALUES.includes(normalizedAction))
            return

        const timeout = action.timeout === null || action.timeout === undefined ? null : Math.trunc(action.timeout / 60)

        if (actionNeedsOpportunity(normalizedAction)) {
            groupedActions.push({
                id: action.id,
                action: normalizedAction,
                timeout,
                journey: action.journey ? String(action.journey) : '',
                journey_column: action.journey_column ? String(action.journey_column) : '',
                opportunity_title: action.opportunity_title || '',
                opportunity_description: action.opportunity_description || '',
                opportunity_value: action.opportunity_value ?? '',
                opportunity_tags: normalizeIdList(action.opportunity_tags || action.tags),
                opportunity_custom_fields: normalizeCustomFields(action.opportunity_custom_fields || action.custom_fields),
            })
            return
        }

        const key = `${normalizedAction}-${timeout}`
        if (!actionByKey.has(key)) {
            const groupedAction = {
                id: action.id,
                action: normalizedAction,
                timeout,
                label_chats: [],
                label_chat_options: [],
                contact_groups: [],
                contact_group_options: [],
            }
            actionByKey.set(key, groupedAction)
            groupedActions.push(groupedAction)
        }

        const groupedAction = actionByKey.get(key)
        if (actionNeedsLabel(normalizedAction) && action.label_chat) {
            const labelId = String(action.label_chat)
            if (!groupedAction.label_chats.includes(labelId))
                groupedAction.label_chats.push(labelId)
            groupedAction.label_chat_options = pushUniqueOption(groupedAction.label_chat_options, {
                id: labelId,
                label: action.label_chat_name || labelId,
            })
        }
        if (actionNeedsGroup(normalizedAction) && action.contact_group) {
            const groupId = String(action.contact_group)
            if (!groupedAction.contact_groups.includes(groupId))
                groupedAction.contact_groups.push(groupId)
            groupedAction.contact_group_options = pushUniqueOption(groupedAction.contact_group_options, {
                id: groupId,
                label: action.contact_group_name || groupId,
            })
        }
    })

    return groupedActions
}

export const getEscalationActionErrors = (actions) => {
    const errors = []

    ;(actions || []).forEach((action, index) => {
        if (!action.action)
            errors.push(`Selecione a ação adicional ${index + 1}.`)
        if (actionNeedsLabel(action.action) && getActionTargetIds(action).length === 0)
            errors.push(`Selecione a etiqueta da ação adicional ${index + 1}.`)
        if (actionNeedsGroup(action.action) && getActionTargetIds(action).length === 0)
            errors.push(`Selecione o segmento da ação adicional ${index + 1}.`)
        if (actionNeedsOpportunity(action.action)) {
            if (!action.journey)
                errors.push(`Selecione a jornada da ação adicional ${index + 1}.`)
            if (!action.journey_column)
                errors.push(`Selecione a coluna da ação adicional ${index + 1}.`)
            if (!action.opportunity_title)
                errors.push(`Informe o título da oportunidade da ação adicional ${index + 1}.`)
        }
    })

    return errors
}

export const buildEscalationActionPayload = (actions) => (actions || []).reduce((payload, action) => {
    if (!ACTION_OPTION_VALUES.includes(action.action))
        return payload

    if (actionNeedsOpportunity(action.action)) {
        payload.push({
            action: action.action,
            timeout: Number(action.timeout || 0) * 60,
            journey: action.journey,
            journey_column: action.journey_column,
            opportunity_title: action.opportunity_title,
            opportunity_description: action.opportunity_description,
            opportunity_value: action.opportunity_value,
            opportunity_tags: normalizeIdList(action.opportunity_tags),
            opportunity_custom_fields: normalizeCustomFields(action.opportunity_custom_fields),
        })
        return payload
    }

    getActionTargetIds(action).forEach(targetId => {
        payload.push({
            action: action.action,
            timeout: Number(action.timeout || 0) * 60,
            label_chat: actionNeedsLabel(action.action) ? targetId : null,
            contact_group: actionNeedsGroup(action.action) ? targetId : null,
        })
    })
    return payload
}, [])

const EscalationActions = ({
    actions,
    onChange,
    labels,
    groups,
    journeys,
    onGroupInputChange = () => {},
    onLabelInputChange = () => {},
}) => {
    const [actionInputValues, setActionInputValues] = useState({})
    const [journeyColumns, setJourneyColumns] = useState({})
    const [journeyTags, setJourneyTags] = useState({})
    const [journeyCustomFields, setJourneyCustomFields] = useState({})
    const [tagsOpen, setTagsOpen] = useState({})
    const [customFieldsOpen, setCustomFieldsOpen] = useState({})
    const [showNewCustomFieldForm, setShowNewCustomFieldForm] = useState({})
    const [selectOpen, setSelectOpen] = useState(false)
    const tagsRefs = useRef({})
    const customFieldsRefs = useRef({})

    const updateAction = (index, patch) => {
        onChange(actions.map((action, actionIndex) => (
            actionIndex === index ? { ...action, ...patch } : action
        )))
    }

    const addAction = () => {
        onChange([...(actions || []), getEmptyAction()])
    }

    const removeAction = (index) => {
        onChange(actions.filter((_, actionIndex) => actionIndex !== index))
    }

    const fetchJourneyColumns = async (journeyId) => {
        if (!journeyId || journeyColumns[journeyId])
            return
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/${journeyId}/columns/`)
        if (response.status === 200) {
            setJourneyColumns(current => ({
                ...current,
                [journeyId]: response.data.results || response.data || []
            }))
        }
    }

    const fetchJourneyTags = async (journeyId) => {
        if (!journeyId || journeyTags[journeyId])
            return
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/${journeyId}/tags/`)
        if (response.status === 200) {
            setJourneyTags(current => ({
                ...current,
                [journeyId]: response.data.results || response.data || []
            }))
        }
    }

    const fetchJourneyCustomFields = async (journeyId) => {
        if (!journeyId || journeyCustomFields[journeyId])
            return
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/${journeyId}/custom_fields/`)
        if (response.status === 200) {
            setJourneyCustomFields(current => ({
                ...current,
                [journeyId]: response.data.results || response.data || []
            }))
        }
    }

    const fetchJourneyOpportunityData = (journeyId) => {
        fetchJourneyColumns(journeyId)
        fetchJourneyTags(journeyId)
        fetchJourneyCustomFields(journeyId)
    }

    const updateJourneyTags = (journeyId) => (updater) => {
        setJourneyTags(current => ({
            ...current,
            [journeyId]: typeof updater === 'function' ? updater(current[journeyId] || []) : updater
        }))
    }

    const handleSaveCustomField = async (index, fieldData) => {
        const journeyId = actions[index]?.journey
        if (!journeyId)
            return
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.post(`/api/enterprises/${enterpriseId}/journeys/${journeyId}/custom_fields/`, fieldData)
        if (response.status === 201) {
            setJourneyCustomFields(current => ({
                ...current,
                [journeyId]: [...(current[journeyId] || []), response.data]
            }))
            setShowNewCustomFieldForm(current => ({ ...current, [index]: false }))
            useNotification("Sucesso", "Campo personalizado criado com sucesso", "success")
        }
    }

    useEffect(() => {
        ;(actions || []).forEach(action => {
            if (actionNeedsOpportunity(action.action) && action.journey)
                fetchJourneyOpportunityData(action.journey)
        })
    }, [actions])

    return (
        <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box className="category-title">
                    Ações adicionais
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    onClick={addAction}
                    startIcon={<AddIcon />}
                >
                    Ação
                </Button>
            </Box>
            {(actions || []).map((action, index) => {
                const valueOptions = actionNeedsLabel(action.action) ? (labels || []) : (groups || [])
                const selectedIds = getActionTargetIds(action)
                const savedOptions = getActionTargetOptions(action)
                const inputKey = `${index}-${action.action}`
                const selectedValue = selectedIds.map(selectedId => (
                    valueOptions.find(option => String(option.id) === String(selectedId)) ||
                    savedOptions.find(option => String(option.id) === String(selectedId)) ||
                    { id: selectedId, label: selectedId }
                ))
                const autocompleteOptions = [
                    ...selectedValue.filter(selected => !valueOptions.some(option => String(option.id) === String(selected.id))),
                    ...valueOptions,
                ]
                const availableTags = journeyTags[action.journey] || []
                const availableCustomFields = journeyCustomFields[action.journey] || []
                const opportunityTags = normalizeIdList(action.opportunity_tags)
                const opportunityCustomFields = normalizeCustomFields(action.opportunity_custom_fields)

                return (
                    <Box key={index} display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" gap={1} alignItems="center">
                            <TextField
                                sx={{ width: "70px" }}
                                size="small"
                                label="Minutos"
                                type="text"
                                variant="outlined"
                                value={action.timeout ?? ''}
                                onChange={({ target }) => updateAction(index, { timeout: IntegerOnly(target.value, 0, 86400) })}
                            />
                            <FormControl sx={{ flex: 2 }} size="small">
                                <InputLabel id={`escalation-action-type-${index}`}>Ação</InputLabel>
                                <Select
                                    labelId={`escalation-action-type-${index}`}
                                    label="Ação"
                                    value={action.action}
                                    onChange={({ target }) => updateAction(index, {
                                        action: target.value,
                                        label_chats: [],
                                        label_chat_options: [],
                                        contact_groups: [],
                                        contact_group_options: [],
                                        journey: '',
                                        journey_column: '',
                                        opportunity_title: '',
                                        opportunity_description: '',
                                        opportunity_value: '',
                                        opportunity_tags: [],
                                        opportunity_custom_fields: {},
                                    })}
                                >
                                    {ACTION_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <IconButton onClick={() => removeAction(index)}>
                                <RemoveIcon />
                            </IconButton>
                        </Box>
                        {!actionNeedsOpportunity(action.action) &&
                            <Autocomplete
                                size="small"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                options={autocompleteOptions}
                                value={selectedValue}
                                inputValue={actionInputValues[inputKey] || ''}
                                filterOptions={(options) => options}
                                getOptionLabel={getOptionLabel}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                noOptionsText="Nenhuma opção disponível"
                                onChange={(event, newValue) => {
                                    setActionInputValues(current => ({ ...current, [inputKey]: '' }))
                                    updateAction(index, actionNeedsLabel(action.action) ? {
                                        label_chats: newValue.map(option => String(option.id)),
                                        label_chat_options: newValue.map(option => ({ id: String(option.id), label: getOptionLabel(option) })),
                                        label_chat: '',
                                        label_chat_name: '',
                                    } : {
                                        contact_groups: newValue.map(option => String(option.id)),
                                        contact_group_options: newValue.map(option => ({ id: String(option.id), label: getOptionLabel(option) })),
                                        contact_group: '',
                                        contact_group_name: '',
                                    })
                                }}
                                onInputChange={(event, value, reason) => {
                                    if (reason === 'reset')
                                        return
                                    setActionInputValues(current => ({ ...current, [inputKey]: value }))
                                    if (actionNeedsLabel(action.action))
                                        onLabelInputChange(value)
                                    else
                                        onGroupInputChange(value)
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={actionNeedsLabel(action.action) ? 'Etiquetas' : 'Segmentos'}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                    />
                                )}
                            />
                        }
                        {actionNeedsOpportunity(action.action) &&
                            <>
                                <Box display="flex" gap={1}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <InputLabel id={`escalation-action-journey-${index}`}>Jornada</InputLabel>
                                        <Select
                                            labelId={`escalation-action-journey-${index}`}
                                            label="Jornada"
                                            value={action.journey || ''}
                                            onChange={({ target }) => {
                                                fetchJourneyOpportunityData(target.value)
                                                updateAction(index, {
                                                    journey: target.value,
                                                    journey_column: '',
                                                    opportunity_tags: [],
                                                    opportunity_custom_fields: {},
                                                })
                                            }}
                                        >
                                            {(journeys || []).map(journey => (
                                                <MenuItem key={journey.id} value={String(journey.id)}>{journey.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ flex: 1 }} disabled={!action.journey}>
                                        <InputLabel id={`escalation-action-column-${index}`}>Coluna</InputLabel>
                                        <Select
                                            labelId={`escalation-action-column-${index}`}
                                            label="Coluna"
                                            value={action.journey_column || ''}
                                            onChange={({ target }) => updateAction(index, { journey_column: target.value })}
                                        >
                                            {(journeyColumns[action.journey] || []).map(column => (
                                                <MenuItem key={column.id} value={String(column.id)}>{column.title}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <VariableTextField
                                    fullWidth={true}
                                    size="small"
                                    label="Título da oportunidade"
                                    variant="outlined"
                                    value={action.opportunity_title || ''}
                                    onChange={({ target }) => updateAction(index, { opportunity_title: target.value })}
                                    onVariableSelect={(value) => updateAction(index, {
                                        opportunity_title: `${action.opportunity_title || ''}${value}`
                                    })}
                                />
                                <TextField
                                    size="small"
                                    label="Descrição da oportunidade"
                                    multiline
                                    minRows={2}
                                    value={action.opportunity_description || ''}
                                    onChange={({ target }) => updateAction(index, { opportunity_description: target.value })}
                                />
                                <TextField
                                    size="small"
                                    label="Valor"
                                    type="number"
                                    value={action.opportunity_value ?? ''}
                                    onChange={({ target }) => updateAction(index, { opportunity_value: target.value })}
                                />
                                <Box>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="subtitle1">
                                            Marcadores (opcional)
                                        </Typography>
                                        <IconButton
                                            ref={(element) => { tagsRefs.current[index] = element }}
                                            onClick={() => setTagsOpen(current => ({ ...current, [index]: !current[index] }))}
                                            size="small"
                                            disabled={!action.journey}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {opportunityTags.map((tagId) => {
                                            const tag = availableTags.find(item => String(item.id) === String(tagId))
                                            if (!tag)
                                                return null
                                            return (
                                                <Box
                                                    key={tag.id}
                                                    sx={{
                                                        padding: ".25rem .5rem",
                                                        borderRadius: ".25rem",
                                                        fontSize: ".9em",
                                                        backgroundColor: tag.color,
                                                        cursor: "pointer",
                                                        color: "#fff",
                                                    }}
                                                    onClick={() => updateAction(index, {
                                                        opportunity_tags: opportunityTags.filter(id => String(id) !== String(tagId))
                                                    })}
                                                >
                                                    {tag.name}
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                    <OpportunityMarkers
                                        tagsOpen={!!tagsOpen[index]}
                                        setTagsOpen={(value) => setTagsOpen(current => ({ ...current, [index]: value }))}
                                        allTags={availableTags}
                                        setAllTags={updateJourneyTags(action.journey)}
                                        newTagsRef={{ current: tagsRefs.current[index] }}
                                        setParent={(changeParent) => {
                                            const nextParent = changeParent({
                                                tags: opportunityTags.map(tagId => Number(tagId)),
                                                allTags: availableTags,
                                            })
                                            updateAction(index, { opportunity_tags: normalizeIdList(nextParent.tags) })
                                            if (nextParent.allTags)
                                                setJourneyTags(current => ({ ...current, [action.journey]: nextParent.allTags }))
                                        }}
                                        parent={{
                                            tags: opportunityTags.map(tagId => Number(tagId)),
                                            allTags: availableTags,
                                        }}
                                        selectedJourney={{ id: action.journey }}
                                    />
                                </Box>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="subtitle1">
                                            Campos Personalizados (opcional)
                                        </Typography>
                                        <IconButton
                                            ref={(element) => { customFieldsRefs.current[index] = element }}
                                            onClick={() => setCustomFieldsOpen(current => ({ ...current, [index]: !current[index] }))}
                                            size="small"
                                            disabled={!action.journey}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                    {availableCustomFields.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            Jornada sem campos personalizados.
                                        </Typography>
                                    ) : (
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            {availableCustomFields.map(field => {
                                                const customFieldValue = opportunityCustomFields[field.id] || ''
                                                if (field.field_type === 'select') {
                                                    return (
                                                        <FormControl key={field.id} fullWidth size="small">
                                                            <InputLabel>{field.name}</InputLabel>
                                                            <Select
                                                                label={field.name}
                                                                value={customFieldValue}
                                                                onChange={({ target }) => updateAction(index, {
                                                                    opportunity_custom_fields: {
                                                                        ...opportunityCustomFields,
                                                                        [field.id]: target.value,
                                                                    }
                                                                })}
                                                            >
                                                                {(field.options || []).map(option => (
                                                                    <MenuItem key={option.id || option.value} value={option.value}>{option.label}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )
                                                }
                                                if (field.field_type === 'checkbox') {
                                                    return (
                                                        <FormControl key={field.id} fullWidth size="small">
                                                            <InputLabel>{field.name}</InputLabel>
                                                            <Select
                                                                label={field.name}
                                                                value={customFieldValue}
                                                                onChange={({ target }) => updateAction(index, {
                                                                    opportunity_custom_fields: {
                                                                        ...opportunityCustomFields,
                                                                        [field.id]: target.value,
                                                                    }
                                                                })}
                                                            >
                                                                <MenuItem value="">Sem valor</MenuItem>
                                                                <MenuItem value="true">Sim</MenuItem>
                                                                <MenuItem value="false">Não</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )
                                                }
                                                return (
                                                    <TextField
                                                        key={field.id}
                                                        size="small"
                                                        label={field.name}
                                                        fullWidth
                                                        type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                                                        multiline={field.field_type === 'textarea'}
                                                        minRows={field.field_type === 'textarea' ? 2 : 1}
                                                        InputLabelProps={field.field_type === 'date' ? { shrink: true } : undefined}
                                                        value={customFieldValue}
                                                        onChange={({ target }) => updateAction(index, {
                                                            opportunity_custom_fields: {
                                                                ...opportunityCustomFields,
                                                                [field.id]: target.value,
                                                            }
                                                        })}
                                                    />
                                                )
                                            })}
                                        </Box>
                                    )}
                                    <Popper
                                        open={!!customFieldsOpen[index]}
                                        anchorEl={customFieldsRefs.current[index]}
                                        placement="bottom-start"
                                        sx={{ zIndex: 9999 }}
                                    >
                                        <ClickAwayListener onClickAway={() => {
                                            if (selectOpen)
                                                return
                                            setCustomFieldsOpen(current => ({ ...current, [index]: false }))
                                        }}>
                                            <Paper sx={{ p: 2, minWidth: "300px", zIndex: 10000 }}>
                                                {!showNewCustomFieldForm[index] && (
                                                    <Box>
                                                        {availableCustomFields.length === 0 && (
                                                            <Box sx={{ mb: 2 }}>
                                                                Jornada sem campos personalizados.
                                                            </Box>
                                                        )}
                                                        {availableCustomFields.length > 0 && (
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                                                    Campos existentes:
                                                                </Typography>
                                                                {availableCustomFields.map(field => (
                                                                    <Box key={field.id} sx={{ padding: "0.25rem 0" }}>
                                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                                            • {field.name}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                        <Button
                                                            className="opportunity-btn"
                                                            onClick={() => setShowNewCustomFieldForm(current => ({ ...current, [index]: true }))}
                                                            fullWidth
                                                            size="small"
                                                        >
                                                            <AddIcon />
                                                            Criar campo personalizado
                                                        </Button>
                                                    </Box>
                                                )}
                                                {showNewCustomFieldForm[index] && (
                                                    <NewCustomFieldForm
                                                        onBack={() => setShowNewCustomFieldForm(current => ({ ...current, [index]: false }))}
                                                        onSave={(fieldData) => handleSaveCustomField(index, fieldData)}
                                                        onSelectOpenChange={(isOpen) => setSelectOpen(isOpen)}
                                                    />
                                                )}
                                            </Paper>
                                        </ClickAwayListener>
                                    </Popper>
                                </Box>
                            </>
                        }
                    </Box>
                )
            })}
        </Box>
    )
}

export default EscalationActions
